import { useState, useMemo, useEffect } from 'react'
import { Map, MapControls, MapMarker, MarkerContent, MarkerPopup, MarkerTooltip, useMap } from '@/components/ui/map'
import { redactStationName } from '../../utils/demoRedaction'
import { sanitizeMapPopupHtml } from '../../utils/sanitizeHtml'
import { filterVehicleGroupKeys, isEntityUlid } from '../../utils/vehicleIdStandardise'
import MapLibreGL from 'maplibre-gl'

/**
 * Heatmap data produced by the backend (vehicle_geographic_mapping.py).
 * GPS points come from the same source as Service Coverage and Route Map:
 * cleaned_telematics with filter_valid_coordinates applied (teleportation/invalid points excluded).
 * With date-aware data, each vehicle/type value is { "all": coords, "YYYY-MM-DD": coords };
 * when selectedDate is set we use that key; when selectedMonth is set we use keys in that month; otherwise "all".
 */
function resolveHeatmapPoints(
  entry: unknown,
  selectedMonth?: { year: number; month: number } | null,
  selectedDate?: string | null
): Array<[number, number, number]> {
  if (Array.isArray(entry)) return entry
  if (!entry || typeof entry !== 'object') return []
  const o = entry as Record<string, unknown>
  if (selectedDate && /^\d{4}-\d{2}-\d{2}$/.test(selectedDate)) {
    const val = o[selectedDate]
    if (Array.isArray(val) && val.length > 0) return val as Array<[number, number, number]>
    // No per-day bucket for this date (e.g. ES JSON only has "all"): fall through to month / all
  }
  if (selectedMonth && selectedMonth.month >= 1 && selectedMonth.month <= 12) {
    const prefix = `${selectedMonth.year}-${String(selectedMonth.month).padStart(2, '0')}-`
    const out: Array<[number, number, number]> = []
    for (const key of Object.keys(o)) {
      if (/^\d{4}-\d{2}-\d{2}$/.test(key) && key.startsWith(prefix)) {
        const val = o[key]
        if (Array.isArray(val)) out.push(...(val as Array<[number, number, number]>))
      }
    }
    if (out.length > 0) return out
  }
  if ('all' in o && Array.isArray(o.all)) return o.all as Array<[number, number, number]>
  return []
}
interface HeatmapData {
  center: [number, number]
  zoom: number
  station_markers: Array<{
    lat: number
    lon: number
    name: string
    popup: string
  }>
  // Support both structures:
  // Emergency Services: { "incident": { "Vehicle": {...}, "Type": {...}, "Station": {...} }, "non-operational": {...} }
  // Logistics: { "Vehicle": {...}, "Type": {...} }
  group_data: {
    [key: string]: {
      Vehicle?: { [id: string]: Array<[number, number, number]> }
      Type?: { [type: string]: Array<[number, number, number]> }
      Station?: { [station: string]: Array<[number, number, number]> }
    } | { [id: string]: Array<[number, number, number]> } // Direct structure for Logistics
  }
}

interface GeographicHeatmapProps {
  data: HeatmapData
  /** When true (demo users), hide real depot/customer names from map popups. */
  redact?: boolean
  /** When true, render only controls + layers (no Map); parent must render a single shared Map. */
  embedInMap?: boolean
  /** When Group By is Vehicle Reg, use this to show customerVehicleName in the Value dropdown */
  getVehicleDisplayName?: (vehicleId: string) => string
  /** When set, show only GPS points for dates in this month (uses YYYY-MM-DD keys in data). */
  selectedMonth?: { year: number; month: number } | null
  /** When set, show only GPS points for this single date (YYYY-MM-DD). Takes precedence over selectedMonth. */
  selectedDate?: string | null
  /** Dates to show in the Date dropdown (YYYY-MM-DD). When length > 0, Date dropdown is shown. */
  availableDates?: string[]
  /** Called when user selects a date from the Date dropdown. */
  onSelectedDateChange?: (date: string | null) => void
  /** Controlled: Group By (Vehicle/Type/Station). When provided, selection stays in sync across map tabs. */
  groupBy?: 'Vehicle' | 'Type' | 'Station'
  /** Controlled: selected value in Group By dropdown. */
  selectedGroupValue?: string | null
  /** Controlled: Show All checkbox. */
  showAll?: boolean
  /** Controlled: Activity (incident/non-operational) for emergency services. */
  selectedActivity?: string
  onGroupByChange?: (v: 'Vehicle' | 'Type' | 'Station') => void
  onSelectedGroupValueChange?: (v: string | null) => void
  onShowAllChange?: (v: boolean) => void
  onSelectedActivityChange?: (v: string) => void
}

// Heatmap layer component using MapLibre
function HeatmapLayer({ points, theme = 'light' }: { points: Array<[number, number, number]>, theme?: 'light' | 'dark' }) {
  const { map, isLoaded } = useMap()
  const sourceId = 'heatmap-source'
  const layerId = 'heatmap-layer'
  
  // Color gradient based on theme
  // Light theme: Blue -> Cyan -> Green -> Yellow (matches Folium, no red)
  // Dark theme: Blue -> Cyan -> Yellow -> Orange -> Red (red for max intensity on dark)
  // Reduced opacity to allow background map to show through
  const heatmapColorStops = useMemo(() => {
    if (theme === 'dark') {
      // Dark theme: Use red for maximum intensity (better contrast on dark background)
      // Color stops distributed so hot colors only appear at high density (clustered points)
      // Increased opacity for better visibility on dark background
      return [
        0, 'rgba(0,0,255,0)',          // Transparent blue (invisible at 0 density)
        0.4, 'rgba(0,255,255,1)',   // Cyan - medium-low density - increased from 0.4
        0.6, 'rgba(255,255,0,1)',   // Yellow - medium density - increased from 0.5
        0.8, 'rgba(255,165,0,1)',   // Orange - high density (clustered points) - increased from 0.6
        1, 'rgba(255,0,0,0.8)',        // Red - maximum density (very clustered) - increased from 0.7
      ]
    } else {
      // Light theme: Use same colors as dark theme for consistency
      // Color stops distributed so hot colors only appear at high density (clustered points)
      // Increased opacity for better visibility
      return [
        0, 'rgba(0,0,255,0)',          // Transparent blue (invisible at 0 density)
        0.2, 'rgba(0,0,255,0.5)',     // Blue - low density (sparse points)
        0.4, 'rgba(0,255,255,0.6)',   // Cyan - medium-low density
        0.6, 'rgba(255,255,0,0.7)',   // Yellow - medium density
        0.8, 'rgba(255,165,0,0.8)',   // Orange - high density (clustered points)
        1, 'rgba(255,0,0,0.8)',        // Red - maximum density (very clustered)
      ]
    }
  }, [theme])

  useEffect(() => {
    if (!isLoaded || !map || points.length === 0) return

    // Convert points to GeoJSON format
    // For frequency heatmaps, all points should have equal weight
    // The color intensity will be based on point density (clustering), not individual point intensity
    const features = points.map(([lat, lon]) => ({
      type: 'Feature' as const,
      geometry: {
        type: 'Point' as const,
        coordinates: [lon, lat] as [number, number], // MapLibre uses [lon, lat]
      },
      properties: {
        // No properties needed - heatmap will use density-based coloring
      },
    }))

    const geojson = {
      type: 'FeatureCollection' as const,
      features,
    }

    // Add source if it doesn't exist
    if (!map.getSource(sourceId)) {
      map.addSource(sourceId, {
        type: 'geojson',
        data: geojson,
      })
    } else {
      // Update existing source
      const source = map.getSource(sourceId) as MapLibreGL.GeoJSONSource
      source.setData(geojson)
    }

    // Add heatmap layer if it doesn't exist
        if (!map.getLayer(layerId)) {
          map.addLayer({
            id: layerId,
            type: 'heatmap',
            source: sourceId,
            maxzoom: 15,
            paint: {
              // All points contribute equally to the heatmap
              // The color intensity is based on point density (how many points cluster together)
              // Lower weight ensures single points don't saturate to maximum color
              'heatmap-weight': 0.65,  // Each point contributes 20% - requires ~5 overlapping points for max density
              // heatmap-intensity is a multiplier on top of heatmap-weight
              // Lower intensity prevents single points from showing all colors
              'heatmap-intensity': [
                'interpolate',
                ['linear'],
                ['zoom'],
                0,
                0.5,   // Lower intensity at low zoom
                9,
                1.5,   // Moderate intensity at higher zoom
              ],
           // Color ramp for heatmap - theme-aware
           // Light theme: Blue -> Cyan -> Green -> Yellow (matches Folium)
           // Dark theme: Blue -> Cyan -> Yellow -> Orange -> Red (red for max intensity)
           'heatmap-color': [
             'interpolate',
             ['linear'],
             ['heatmap-density'],
             ...heatmapColorStops
           ],
          // Adjust the heatmap radius by zoom level
          // In MapLibre, radius controls both size and blur effect
          // Larger radius = more blur, smaller radius = sharper detail
          // Folium uses radius=10, blur=15, so we adjust radius to create similar blur effect
          // At lower zoom: larger radius (more blur, smoother blending)
          // At higher zoom: smaller radius (less blur, sharper detail)
          'heatmap-radius': [
            'interpolate',
            ['linear'],
            ['zoom'],
            0,
            20,    // Large radius at low zoom (zoomed out) - creates smooth, blurred effect
            5,
            15,    // Medium-large radius at mid-low zoom
            10,
            10,    // Medium radius at mid zoom - balanced detail and blur
            15,
            5,    // Smaller radius at high zoom (zoomed in) - sharper, more detailed
            20,
            3,     // Small radius at very high zoom - maximum detail, minimal blur
          ],
          // Keep heatmap visible at all zoom levels but with reduced opacity
          // Lower opacity allows background map to show through
          'heatmap-opacity': [
            'interpolate',
            ['linear'],
            ['zoom'],
            0,
            0.5,
            10,
            0.6,
            22,
            0.6,
          ],
        },
      })
    }

    return () => {
      try {
        if (map.getLayer(layerId)) map.removeLayer(layerId)
        if (map.getSource(sourceId)) map.removeSource(sourceId)
      } catch {
        // ignore
      }
    }
  }, [isLoaded, map, points, sourceId, layerId, heatmapColorStops])

  return null
}

// Compute min/max without spreading (avoids "Maximum call stack size exceeded" for large arrays)
function boundsFromPoints(points: Array<[number, number, number]>): { minLat: number; maxLat: number; minLon: number; maxLon: number } | null {
  if (points.length === 0) return null
  let minLat = points[0]![0]
  let maxLat = points[0]![0]
  let minLon = points[0]![1]
  let maxLon = points[0]![1]
  for (let i = 1; i < points.length; i++) {
    const [lat, lon] = points[i]!
    if (lat < minLat) minLat = lat
    if (lat > maxLat) maxLat = lat
    if (lon < minLon) minLon = lon
    if (lon > maxLon) maxLon = lon
  }
  return { minLat, maxLat, minLon, maxLon }
}

// Component to auto-zoom to heatmap bounds
function AutoZoomToHeatmap({ points }: { points: Array<[number, number, number]> }) {
  const { map, isLoaded } = useMap()

  useEffect(() => {
    if (!isLoaded || !map || points.length === 0) return

    const b = boundsFromPoints(points)
    if (!b) return

    let { minLat, maxLat, minLon, maxLon } = b
    // Enforce minimum span so the map is zoomed out enough for heatmap density to be visible
    const minSpanDeg = 0.04
    let latSpan = maxLat - minLat
    let lonSpan = maxLon - minLon
    if (latSpan < minSpanDeg) {
      const half = (minSpanDeg - latSpan) / 2
      minLat -= half
      maxLat += half
    }
    if (lonSpan < minSpanDeg) {
      const half = (minSpanDeg - lonSpan) / 2
      minLon -= half
      maxLon += half
    }
    latSpan = maxLat - minLat
    lonSpan = maxLon - minLon
    // Use larger padding (0.4) so initial view is more zoomed out and heatmap color renders
    const pad = 0.4
    const latPadding = latSpan * pad
    const lonPadding = lonSpan * pad

    const bounds = [
      [minLon - lonPadding, minLat - latPadding],
      [maxLon + lonPadding, maxLat + latPadding],
    ] as [[number, number], [number, number]]

    map.fitBounds(bounds, {
      padding: { top: 50, bottom: 50, left: 50, right: 50 },
      duration: 500,
    })
  }, [isLoaded, map, points])

  return null
}

export function GeographicHeatmap({ data, redact: _redact = false, embedInMap = false, getVehicleDisplayName, selectedMonth = null, selectedDate = null, availableDates = [], onSelectedDateChange, groupBy: groupByProp, selectedGroupValue: selectedGroupValueProp, showAll: showAllProp, selectedActivity: selectedActivityProp, onGroupByChange, onSelectedGroupValueChange, onShowAllChange, onSelectedActivityChange }: GeographicHeatmapProps) {
  // Safe access to group_data (avoid crash when data is missing or when Show All combines data)
  const groupDataSafe = useMemo(() => {
    const g = data?.group_data
    if (!g || typeof g !== 'object') return {}
    return g
  }, [data?.group_data])

  // Detect data structure: Logistics has no activities, Emergency Services has activities
  const hasActivities = useMemo(() => {
    return 'incident' in groupDataSafe || 'non-operational' in groupDataSafe
  }, [groupDataSafe])

  // Get available group types (Vehicle, Type, Station)
  const availableGroupTypes = useMemo(() => {
    const groupTypes: Array<'Vehicle' | 'Type' | 'Station'> = ['Vehicle', 'Type']
    // Check if Station exists in the data structure
    if (hasActivities) {
      const activityData = groupDataSafe['incident'] || groupDataSafe['non-operational']
      if (activityData && typeof activityData === 'object' && 'Station' in activityData) {
        groupTypes.push('Station')
      }
    } else {
      // For logistics, check directly in group_data
      if ('Station' in groupDataSafe) {
        groupTypes.push('Station')
      }
    }
    return groupTypes
  }, [groupDataSafe, hasActivities])

  const [selectedActivityInternal, setSelectedActivityInternal] = useState<string>(hasActivities ? 'incident' : '')
  const [groupByInternal, setGroupByInternal] = useState<'Vehicle' | 'Type' | 'Station'>(availableGroupTypes[0] || 'Vehicle')
  const [selectedGroupValueInternal, setSelectedGroupValueInternal] = useState<string | null>(null)
  const [showAllInternal, setShowAllInternal] = useState<boolean>(false)
  const selectedActivity = selectedActivityProp ?? selectedActivityInternal
  const setSelectedActivity = onSelectedActivityChange ? (v: string) => { setSelectedActivityInternal(v); onSelectedActivityChange(v) } : setSelectedActivityInternal
  const groupBy = groupByProp ?? groupByInternal
  const setGroupBy = onGroupByChange ? (v: 'Vehicle' | 'Type' | 'Station') => { setGroupByInternal(v); onGroupByChange(v) } : setGroupByInternal
  const selectedGroupValue = selectedGroupValueProp !== undefined ? selectedGroupValueProp : selectedGroupValueInternal
  const setSelectedGroupValue = onSelectedGroupValueChange ? (v: string | null) => { setSelectedGroupValueInternal(v); onSelectedGroupValueChange(v) } : setSelectedGroupValueInternal
  const showAll = showAllProp !== undefined ? showAllProp : showAllInternal
  const setShowAll = onShowAllChange ? (v: boolean) => { setShowAllInternal(v); onShowAllChange(v) } : setShowAllInternal
  const mapTheme: 'light' | 'dark' = 'dark' // Fixed to dark theme

  // Get available group values for dropdown
  const availableGroupValues = useMemo(() => {
    let groupData: { [key: string]: Array<[number, number, number]> } | undefined
    
    if (hasActivities) {
      const activityData = groupDataSafe[selectedActivity]
      if (!activityData || typeof activityData !== 'object') return []
      // Type guard: check if it's the Emergency Services structure
      if ('Vehicle' in activityData || 'Type' in activityData || 'Station' in activityData) {
        const typedActivityData = activityData as {
          Vehicle?: { [id: string]: Array<[number, number, number]> }
          Type?: { [type: string]: Array<[number, number, number]> }
          Station?: { [station: string]: Array<[number, number, number]> }
        }
        groupData = typedActivityData[groupBy] as { [key: string]: Array<[number, number, number]> } | undefined
      }
    } else {
      // Logistics: direct access to group_data
      const directData = groupDataSafe[groupBy]
      // Type guard: check if it's the Logistics structure (direct object)
      if (directData && typeof directData === 'object' && !('Vehicle' in directData || 'Type' in directData)) {
        groupData = directData as { [key: string]: Array<[number, number, number]> }
      }
    }
    
    if (!groupData) return []
    return filterVehicleGroupKeys(Object.keys(groupData).sort(), groupBy)
  }, [groupDataSafe, selectedActivity, groupBy, hasActivities])

  // Auto-select first value when groupBy or activity changes
  useEffect(() => {
    if (!showAll && availableGroupValues.length > 0) {
      if (!selectedGroupValue || !availableGroupValues.includes(selectedGroupValue)) {
        setSelectedGroupValue(availableGroupValues[0])
      }
    } else if (availableGroupValues.length === 0) {
      setSelectedGroupValue(null)
    }
  }, [availableGroupValues, showAll, selectedGroupValue])

  // Get current heatmap points based on filters
  // Filter out groups with less than 100 points
  const heatmapPoints = useMemo(() => {
    let groupData: { [key: string]: Array<[number, number, number]> } | undefined
    
    if (hasActivities) {
      const activityData = groupDataSafe[selectedActivity]
      if (!activityData || typeof activityData !== 'object') return []
      // Type guard: check if it's the Emergency Services structure
      if ('Vehicle' in activityData || 'Type' in activityData || 'Station' in activityData) {
        const typedActivityData = activityData as {
          Vehicle?: { [id: string]: Array<[number, number, number]> }
          Type?: { [type: string]: Array<[number, number, number]> }
          Station?: { [station: string]: Array<[number, number, number]> }
        }
        groupData = typedActivityData[groupBy] as { [key: string]: Array<[number, number, number]> } | undefined
      }
    } else {
      // Logistics: direct access to group_data
      const directData = groupDataSafe[groupBy]
      // Type guard: check if it's the Logistics structure (direct object)
      if (directData && typeof directData === 'object' && !('Vehicle' in directData || 'Type' in directData)) {
        groupData = directData as { [key: string]: Array<[number, number, number]> }
      }
    }
    
    if (!groupData) return []

    if (showAll) {
      // Combine all groups, but filter out groups with less than 100 points
      return Object.entries(groupData)
        .filter(([key]) => groupBy !== 'Vehicle' || !isEntityUlid(key))
        .map(([_, entry]) => resolveHeatmapPoints(entry, selectedMonth, selectedDate))
        .filter((points) => points.length >= 100)
        .flatMap((points) => points)
    } else if (selectedGroupValue && groupData[selectedGroupValue]) {
      const points = resolveHeatmapPoints(groupData[selectedGroupValue], selectedMonth, selectedDate)
      // Only show if this group has at least 100 points
      return points.length >= 100 ? points : []
    }

    return []
  }, [groupDataSafe, selectedActivity, groupBy, selectedGroupValue, showAll, hasActivities, selectedMonth, selectedDate])

  // Downsample for rendering when "Show All" produces too many points (avoids stack overflow and keeps map performant)
  const MAX_HEATMAP_POINTS = 50000
  const heatmapPointsForRender = useMemo(() => {
    if (heatmapPoints.length <= MAX_HEATMAP_POINTS) return heatmapPoints
    const step = Math.ceil(heatmapPoints.length / MAX_HEATMAP_POINTS)
    const out: Array<[number, number, number]> = []
    for (let i = 0; i < heatmapPoints.length; i += step) out.push(heatmapPoints[i]!)
    return out
  }, [heatmapPoints])

  // Check if we have enough data points
  const hasInsufficientData = heatmapPoints.length > 0 && heatmapPoints.length < 100

  // When redaction is enabled (demo users), avoid exposing real depot/customer names in station popups.
  const stationMarkers = useMemo(() => {
    const markers = Array.isArray(data.station_markers) ? data.station_markers : []
    if (!_redact) return markers
    return markers.map((marker, idx) => ({
      ...marker,
      popup: `Station ${String.fromCharCode(65 + (idx % 26))}`,
    }))
  }, [data.station_markers, _redact])

  return (
    <div className="relative w-full h-full pointer-events-none">
      {/* Control Panel */}
      <div className="absolute top-4 left-4 z-10 bg-card p-4 rounded-lg shadow-lg border border-border min-w-70 pointer-events-auto">
        <h4 className="font-bold mb-3 text-white text-center">Heatmap</h4>
        
        <div className="space-y-3">
          {/* Date dropdown - before Group By */}
          {availableDates.length > 0 && onSelectedDateChange && (
            <div>
              <label className="block text-sm font-medium mb-1 text-foreground">Date:</label>
              <select
                value={selectedDate ?? ''}
                onChange={(e) => onSelectedDateChange(e.target.value || null)}
                className="w-full p-2 border border-border rounded bg-input text-white text-sm"
              >
                <option value="">All dates in period</option>
                {availableDates.map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            </div>
          )}

          {/* Activity dropdown - only show if data has activities (Emergency Services) */}
          {hasActivities && (
            <div>
              <label className="block text-sm font-medium mb-1 text-foreground">Activity:</label>
              <select
                value={selectedActivity}
                onChange={(e) => setSelectedActivity(e.target.value)}
                className="w-full p-2 border border-border rounded bg-input text-white text-sm"
              >
                <option value="incident">Incident</option>
                <option value="non-operational">Non-Operational</option>
              </select>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium mb-1 text-foreground">Group By:</label>
            <select
              value={groupBy}
              onChange={(e) => {
                const newGroupBy = e.target.value as 'Vehicle' | 'Type' | 'Station'
                setGroupBy(newGroupBy)
                // Auto-select first value after groupBy changes
                let groupData: { [key: string]: Array<[number, number, number]> } | undefined
                
                if (hasActivities) {
                  const activityData = groupDataSafe[selectedActivity]
                  if (activityData && typeof activityData === 'object') {
                    // Type guard: check if it's the Emergency Services structure
                    if ('Vehicle' in activityData || 'Type' in activityData || 'Station' in activityData) {
                      const typedActivityData = activityData as {
                        Vehicle?: { [id: string]: Array<[number, number, number]> }
                        Type?: { [type: string]: Array<[number, number, number]> }
                        Station?: { [station: string]: Array<[number, number, number]> }
                      }
                      groupData = typedActivityData[newGroupBy] as { [key: string]: Array<[number, number, number]> } | undefined
                    }
                  }
                } else {
                  // Logistics: direct access
                  const directData = groupDataSafe[newGroupBy]
                  // Type guard: check if it's the Logistics structure (direct object)
                  if (directData && typeof directData === 'object' && !('Vehicle' in directData || 'Type' in directData)) {
                    groupData = directData as { [key: string]: Array<[number, number, number]> }
                  }
                }
                
                if (groupData) {
                  const keys = Object.keys(groupData).sort()
                  setSelectedGroupValue(keys.length > 0 ? keys[0] : null)
                } else {
                  setSelectedGroupValue(null)
                }
              }}
              className="w-full p-2 border border-border rounded bg-input text-white text-sm"
            >
              <option value="Vehicle">Vehicle Reg</option>
              <option value="Type">Vehicle Type</option>
              {availableGroupTypes.includes('Station') && (
                <option value="Station">Station</option>
              )}
            </select>
          </div>

          {!showAll && (
            <div>
              <label className="block text-sm font-medium mb-1 text-foreground">Value:</label>
              <select
                value={selectedGroupValue || ''}
                onChange={(e) => setSelectedGroupValue(e.target.value || null)}
                className="w-full p-2 border border-border rounded bg-input text-white text-sm"
              >
                <option value="">Select...</option>
                {availableGroupValues.map(key => {
                  const label =
                    groupBy === 'Vehicle' && getVehicleDisplayName
                      ? getVehicleDisplayName(key)
                      : groupBy === 'Station'
                        ? redactStationName(key, _redact ?? false)
                        : key
                  return (
                    <option key={key} value={key}>{label}</option>
                  )
                })}
              </select>
            </div>
          )}

          <div>
            <label className="flex items-center text-sm text-foreground">
              <input
                type="checkbox"
                checked={showAll}
                onChange={(e) => setShowAll(e.target.checked)}
                className="mr-2"
              />
              Show All
            </label>
          </div>

          {heatmapPoints.length > 0 && (
            <div className="text-xs pt-2 border-t border-border">
              <div className="text-muted-foreground">
                Points: {heatmapPoints.length.toLocaleString()}
                {heatmapPointsForRender.length < heatmapPoints.length && (
                  <span className="text-warning ml-1">
                    (showing {heatmapPointsForRender.length.toLocaleString()} for performance)
                  </span>
                )}
              </div>
              {hasInsufficientData && (
                <div className="text-warning mt-1 font-medium">
                  ⚠️ Not enough data points. Generated heatmap may be inaccurate.
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Map or embed layers only (parent provides Map when embedInMap) */}
      {embedInMap ? (
        <>
          {heatmapPointsForRender.length > 0 && (
            <HeatmapLayer points={heatmapPointsForRender} theme={mapTheme} />
          )}
          <AutoZoomToHeatmap points={heatmapPointsForRender} />
        </>
      ) : (
        <div className="w-full h-full">
          <Map 
            center={[data.center[1], data.center[0]]} // MapLibre uses [lon, lat]
            zoom={data.zoom} 
            theme={mapTheme}
          >
            <MapControls
              position="bottom-right"
              showZoom
              showCompass
              showLocate
              showFullscreen
            />
            {stationMarkers.map((marker, idx) => (
              <MapMarker
                key={idx}
                longitude={marker.lon}
                latitude={marker.lat}
              >
                <MarkerContent>
                  {hasActivities ? (
                    <div className="relative cursor-pointer">
                      <svg width="36" height="48" viewBox="0 0 24 32" fill="none" xmlns="http://www.w3.org/2000/svg" className="drop-shadow-lg" style={{ filter: 'drop-shadow(0 0 3px rgba(255,255,255,0.8))' }}>
                        <path d="M12 0C7.58 0 4 3.58 4 8C4 14 12 24 12 24C12 24 20 14 20 8C20 3.58 16.42 0 12 0Z" fill="#DC2626" stroke="#FFFFFF" strokeWidth="1.5" />
                        <rect x="8" y="10" width="8" height="6" fill="#FFFFFF" />
                        <rect x="9" y="12" width="2" height="2" fill="#DC2626" />
                        <rect x="13" y="12" width="2" height="2" fill="#DC2626" />
                        <polygon points="10,8 12,6 14,8" fill="#FFFFFF" />
                      </svg>
                    </div>
                  ) : (
                    <div className="relative cursor-pointer">
                      <svg width="36" height="48" viewBox="0 0 24 32" fill="none" xmlns="http://www.w3.org/2000/svg" className="drop-shadow-lg" style={{ filter: 'drop-shadow(0 0 3px rgba(255,255,255,0.8))' }}>
                        <path d="M12 0C7.58 0 4 3.58 4 8C4 14 12 24 12 24C12 24 20 14 20 8C20 3.58 16.42 0 12 0Z" fill="#F59E0B" stroke="#FFFFFF" strokeWidth="1.5" />
                        <rect x="7" y="10" width="10" height="8" fill="#FFFFFF" stroke="#F59E0B" strokeWidth="0.5" />
                        <rect x="8" y="12" width="2" height="2" fill="#F59E0B" />
                        <rect x="11" y="12" width="2" height="2" fill="#F59E0B" />
                        <rect x="14" y="12" width="2" height="2" fill="#F59E0B" />
                        <line x1="7" y1="14" x2="17" y2="14" stroke="#F59E0B" strokeWidth="0.5" />
                        <polygon points="9,10 12,7 15,10" fill="#FFFFFF" />
                      </svg>
                    </div>
                  )}
                </MarkerContent>
                <MarkerTooltip className="!p-0 !m-0 bg-background text-white rounded shadow-lg">
                  <div className="px-2 py-1 text-xs" dangerouslySetInnerHTML={{ __html: sanitizeMapPopupHtml(marker.popup) }} />
                </MarkerTooltip>
                {hasActivities && (
                  // Light-surface popup: the token set is dark-only, so this
                  // keeps the raw palette (escape hatch, spec US11).
                  <MarkerPopup className="bg-white text-gray-900 max-w-50">
                    <div className="text-sm" dangerouslySetInnerHTML={{ __html: sanitizeMapPopupHtml(marker.popup) }} />
                  </MarkerPopup>
                )}
              </MapMarker>
            ))}
            {heatmapPointsForRender.length > 0 && (
              <HeatmapLayer points={heatmapPointsForRender} theme={mapTheme} />
            )}
            <AutoZoomToHeatmap points={heatmapPointsForRender} />
          </Map>
        </div>
      )}
    </div>
  )
}
